import React, { useContext } from "react";
import { useHistory, useLocation } from "react-router";

import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faChevronLeft } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { Popup } from "semantic-ui-react";

import ViewportContext from "~/common/ViewportContext";
import { TeamsContext } from "~/integrations/teams/teamsContext";
import { inCalendarMode } from "~/lib/utils";
import { Button } from "~/ui";
import ArrowDropdown from "~/ui/ArrowDropdown";
import IconButton from "~/ui/legacy/IconButton";

import styles from "./css/PageHeader.module.css";

type HeaderActionType = Omit<React.ComponentPropsWithoutRef<typeof Button>, "slim"> & {
	disabledText?: string;
	popupText?: string;
	slim?: boolean;
};

export function HeaderAction({
	className,
	disabled,
	disabledText,
	slim = false,
	variant = "secondary",
	...props
}: HeaderActionType) {
	const button = (
		<Button
			size={slim ? "normal" : "wide"}
			variant={variant}
			disabled={disabled}
			className={cx(className, styles.actionButton)}
			{...props}
		/>
	);

	if (disabled && disabledText) {
		return (
			<Popup
				inverted
				size="tiny"
				content={disabledText}
				position="bottom center"
				className={styles.popup}
				trigger={<div>{button}</div>}
			/>
		);
	}
	return button;
}

export const GenericBackButton: React.FC<{ content?: React.ReactNode; backFn?: () => void }> = ({
	content,
	backFn,
}) => {
	const history = useHistory();
	return (
		<Button className={styles.backButton} onClick={backFn || history.goBack} size="normal" variant="tertiary">
			{content || <FontAwesomeIcon icon={faChevronLeft} />}
		</Button>
	);
};

export type Props = React.HTMLProps<HTMLDivElement> & {
	subject: React.ReactNode;
	actions: React.ReactNode[];
	mobileActions?: React.ReactNode[];
	moreActions?: React.ReactNode[];
	mobileShowActionButton?: boolean;
	backButton?: React.ReactNode;
	text?: string;
	onDropdownOpenChange?: (open: boolean) => void;
	embedded?: boolean;
};

export default function PageHeader({
	subject,
	actions,
	mobileActions,
	moreActions,
	className,
	backButton,
	mobileShowActionButton = false,
	text = "More actions",
	onDropdownOpenChange,
	embedded,
	...headerProps
}: Props) {
	const location = useLocation<{ isNewAdhocEvent?: boolean }>();
	const { isMobile } = useContext(ViewportContext);
	const teamsContext = useContext(TeamsContext);

	const dropDownProps: React.ComponentProps<typeof ArrowDropdown> = {
		onOpenChange: onDropdownOpenChange,
		heading: "Actions",
		...(isMobile ? { trigger: <IconButton className={styles.kebab} icon={faEllipsisV} /> } : { text }),
	};

	const dropdown = moreActions && moreActions.length > 0 && (
		<ArrowDropdown
			className={cx(dropDownProps.className || "", { [styles.moreActionsTeams]: !!teamsContext })}
			{...dropDownProps}
		>
			{isMobile && mobileActions && <div className={styles.mobileActions}>{mobileActions}</div>}
			{moreActions}
		</ArrowDropdown>
	);

	let actionsSection;
	if ((!embedded && !inCalendarMode) || (teamsContext && !teamsContext.isTeamsSidePanel)) {
		if (isMobile && !mobileShowActionButton) {
			actionsSection = dropdown;
		} else {
			actionsSection = (
				<section className={styles.actions}>
					{actions}
					{dropdown}
				</section>
			);
		}
	} else if (location.state?.isNewAdhocEvent) {
		// always show share button for new adhoc events
		actionsSection = <section className={styles.actions}>{actions}</section>;
	}

	return (
		<header className={cx(styles.header, className)} {...headerProps}>
			{backButton}
			{subject}
			{actionsSection}
		</header>
	);
}
